import { dist } from '@tol/core/core';
import { stationTiers, stationsGeoJSON, stopsGeoJSON } from '@tol/core/layers';
import type { LineId, Network, StationId } from '@tol/core/types';

type Points = GeoJSON.FeatureCollection<GeoJSON.Point>;
interface StationPresentation {
  readonly visible: ReadonlySet<StationId>;
  readonly labels: readonly StationId[];
  readonly stations: Points | null;
  readonly stops: Points | null;
}

// Retained geometry is independent of active label eligibility: two nearby
// networks can have the same station name, and the preferred label must move
// to the active network when the other becomes inactive.
export function networkStations(
  lines: Parameters<typeof stationsGeoJSON>[2],
): (active: Network, retained: Network, hidden: ReadonlySet<LineId>) => StationPresentation {
  let previous: Network | null = null;
  let previousHidden = '';
  return (active, retained, hidden) => {
    const activeTiers = stationTiers(active, lines, hidden);
    const visible = activeTiers.stations;
    const labels = [
      ...stationsGeoJSON(active, visible, lines, dist).features,
      ...stationsGeoJSON(active, activeTiers.stops, lines, dist, 100).features,
    ].flatMap((f: { readonly properties: Readonly<Record<string, unknown>> | null }) =>
      f.properties?.['label'] === true && typeof f.properties['id'] === 'string' ? [f.properties['id']] : [],
    );
    const hiddenKey = JSON.stringify([...hidden].toSorted());
    let stations: Points | null = null;
    let stops: Points | null = null;
    if (retained !== previous || hiddenKey !== previousHidden) {
      previous = retained;
      previousHidden = hiddenKey;
      const tiers = stationTiers(retained, lines, hidden);
      stations = stationsGeoJSON(retained, tiers.stations, lines, dist);
      // Eligibility comes from the active label filter, not from deduplicating
      // the retained superset. Keep the existing layer's label clause true.
      for (const feature of stations.features) {
        if (feature.properties) feature.properties['label'] = true;
      }
      stops = stopsGeoJSON(retained, tiers.stops);
    }
    return { visible, labels, stations, stops };
  };
}
